import {
  CONCEPTS,
  FINDY_DOCS,
  RRF_K,
  STOPWORDS,
  SYNONYMS,
  type FindyDoc,
} from '../../../data/findy-index';
import type { FusedRow, Ranked, Retrieval } from './types';

/*
 * The toy hybrid retrieval. Real computation, small:
 *   dense   = cosine over the twelve concept axes, query terms mapped through SYNONYMS
 *   keyword = stemmed token overlap, matched terms over query terms
 *   fused   = reciprocal-rank fusion at k = RRF_K over both rankings
 * A query answers only when the fused winner also carries a minimum signal in one leg,
 * so nonsense refuses and the trap chip refuses structurally (no synonym, no token).
 */

const TOP = 4;
const MIN_FUSED = 0.016;
const MIN_DENSE = 0.7;
const MIN_KEYWORD = 0.5;

/* Crude stemming: plural s, ing, ed. */
export function stem(word: string): string {
  if (word.length > 5 && word.endsWith('ing')) return word.slice(0, -3);
  if (word.length > 4 && word.endsWith('ed')) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/* Query terms: tokenized, stopwords removed, stemmed, unique, in order. */
export function queryTerms(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tokenize(text)) {
    if (STOPWORDS.has(raw)) continue;
    const term = stem(raw);
    if (!seen.has(term)) {
      seen.add(term);
      out.push(term);
    }
  }
  return out;
}

const DOC_TOKENS: Set<string>[] = FINDY_DOCS.map(doc => new Set(doc.tokens.map(stem)));

function conceptVector(terms: string[]): number[] {
  const vec = new Array<number>(CONCEPTS.length).fill(0);
  for (const term of terms) {
    const entries = SYNONYMS[term] ?? SYNONYMS[`${term}e`];
    if (!entries) continue;
    for (const [axis, weight] of entries) vec[axis] += weight;
  }
  return vec;
}

function cosine(a: number[], b: number[]): number {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (na === 0 || nb === 0) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

function rank(scores: { doc: FindyDoc; score: number; label: string }[]): Ranked[] {
  return scores
    .map((s, index) => ({ ...s, index }))
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, TOP)
    .map((s, i) => ({ doc: s.doc, score: s.score, rank: i + 1, label: s.label }));
}

function denseLeg(terms: string[]): Ranked[] {
  const q = conceptVector(terms);
  return rank(
    FINDY_DOCS.map(doc => {
      const score = cosine(q, doc.concept);
      return { doc, score, label: score.toFixed(2) };
    })
  );
}

function keywordLeg(terms: string[]): Ranked[] {
  if (terms.length === 0) return [];
  return rank(
    FINDY_DOCS.map((doc, i) => {
      const matched = terms.filter(t => DOC_TOKENS[i].has(t)).length;
      return { doc, score: matched / terms.length, label: `${matched}/${terms.length}` };
    })
  );
}

function fuse(dense: Ranked[], keyword: Ranked[]): FusedRow[] {
  const rows = new Map<string, FusedRow>();
  const add = (r: Ranked, leg: 'dense' | 'keyword') => {
    const row = rows.get(r.doc.file) ?? {
      doc: r.doc,
      score: 0,
      denseRank: null,
      keywordRank: null,
      formula: '',
      aboveThreshold: false,
    };
    row.score += 1 / (RRF_K + r.rank);
    if (leg === 'dense') row.denseRank = r.rank;
    else row.keywordRank = r.rank;
    rows.set(r.doc.file, row);
  };
  dense.forEach(r => add(r, 'dense'));
  keyword.forEach(r => add(r, 'keyword'));

  const denseScore = (file: string) => dense.find(r => r.doc.file === file)?.score ?? 0;
  const keywordScore = (file: string) => keyword.find(r => r.doc.file === file)?.score ?? 0;

  return Array.from(rows.values())
    .sort((a, b) => b.score - a.score || FINDY_DOCS.indexOf(a.doc) - FINDY_DOCS.indexOf(b.doc))
    .slice(0, TOP)
    .map(row => {
      const parts = [row.denseRank, row.keywordRank]
        .filter((r): r is number => r !== null)
        .map(r => `1/(${RRF_K}+${r})`);
      const strongSignal =
        denseScore(row.doc.file) >= MIN_DENSE || keywordScore(row.doc.file) >= MIN_KEYWORD;
      return {
        ...row,
        formula: `rrf = ${parts.join(' + ')} = ${row.score.toFixed(4)}`,
        aboveThreshold: row.score >= MIN_FUSED && strongSignal,
      };
    });
}

function normalizeQuestion(query: string): string {
  return query
    .trim()
    .toLowerCase()
    .replace(/[?!.]+$/, '')
    .replace(/\s+/g, ' ');
}

export function retrieve(query: string): Retrieval {
  const terms = queryTerms(query);
  const dense = denseLeg(terms);
  const keyword = keywordLeg(terms);
  /* Similarity finds documents; it cannot establish that a fixed answer addresses the question.
   * Keep this local demo conservative: only reviewed questions may produce an answer. */
  const normalized = normalizeQuestion(query);
  const fused = fuse(dense, keyword).map(row => ({
    ...row,
    aboveThreshold:
      row.aboveThreshold &&
      row.doc.supportedQuestions.some(question => normalizeQuestion(question) === normalized),
  }));
  const cleared = fused.filter(r => r.aboveThreshold);
  return {
    query,
    terms,
    dense,
    keyword,
    fused,
    verdict: cleared.length > 0 ? cleared[0].doc : null,
    aboveThreshold: cleared.length,
  };
}

export const EMPTY_RETRIEVAL: Retrieval = retrieve('');
